import { useState, useRef, useCallback, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";
import { Button } from "@/shared/ui/button";
import { cn } from "@/shared/lib/utils";
import { FirebaseImage } from "@/shared/components/FirebaseImage";

interface DraggablePhotoViewerProps {
  photos: Array<{ url: string; name: string }>;
  initialIndex: number;
  title: string;
  onClose: () => void;
}

export function DraggablePhotoViewer({
  photos,
  initialIndex,
  title,
  onClose,
}: DraggablePhotoViewerProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);

  // Window dragging (via header)
  const [windowPos, setWindowPos] = useState({ x: 0, y: 0 });
  const [isDraggingWindow, setIsDraggingWindow] = useState(false);
  const [windowDragStart, setWindowDragStart] = useState({ x: 0, y: 0 });

  // Image panning (when zoomed)
  const [imgOffset, setImgOffset] = useState({ x: 0, y: 0 });
  const [isDraggingImg, setIsDraggingImg] = useState(false);
  const [imgDragStart, setImgDragStart] = useState({ x: 0, y: 0 });

  const headerRef = useRef<HTMLDivElement>(null);

  // ── Window drag ──────────────────────────────────────────
  const handleWindowMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (headerRef.current?.contains(e.target as Node)) {
        // Don't drag if clicking a button inside the header
        if ((e.target as HTMLElement).closest("button")) return;
        setIsDraggingWindow(true);
        setWindowDragStart({
          x: e.clientX - windowPos.x,
          y: e.clientY - windowPos.y,
        });
        e.preventDefault();
      }
    },
    [windowPos]
  );

  useEffect(() => {
    if (!isDraggingWindow) return;
    const onMove = (e: MouseEvent) => {
      setWindowPos({
        x: e.clientX - windowDragStart.x,
        y: e.clientY - windowDragStart.y,
      });
    };
    const onUp = () => setIsDraggingWindow(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDraggingWindow, windowDragStart]);

  // ── Image drag (pan when zoomed) ─────────────────────────
  const handleImgMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoom <= 1) return;
      setIsDraggingImg(true);
      setImgDragStart({
        x: e.clientX - imgOffset.x,
        y: e.clientY - imgOffset.y,
      });
      e.preventDefault();
    },
    [zoom, imgOffset]
  );

  useEffect(() => {
    if (!isDraggingImg) return;
    const onMove = (e: MouseEvent) => {
      setImgOffset({
        x: e.clientX - imgDragStart.x,
        y: e.clientY - imgDragStart.y,
      });
    };
    const onUp = () => setIsDraggingImg(false);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [isDraggingImg, imgDragStart]);

  // ── Keyboard navigation ──────────────────────────────────
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") goToPrev();
      if (e.key === "ArrowRight") goToNext();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [currentIndex, photos.length]);

  const goToPrev = () => {
    setCurrentIndex((i) => (i > 0 ? i - 1 : photos.length - 1));
    setZoom(1);
    setImgOffset({ x: 0, y: 0 });
  };

  const goToNext = () => {
    setCurrentIndex((i) => (i < photos.length - 1 ? i + 1 : 0));
    setZoom(1);
    setImgOffset({ x: 0, y: 0 });
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.5, 10));
  const zoomOut = () => {
    setZoom((z) => {
      const next = Math.max(z - 0.5, 0.5);
      if (next <= 1) setImgOffset({ x: 0, y: 0 });
      return next;
    });
  };

  // Scroll-to-zoom on the image area (native listener to allow preventDefault)
  const imageAreaRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = imageAreaRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      const delta = e.deltaY > 0 ? -0.25 : 0.25;
      setZoom((z) => {
        const next = Math.min(Math.max(z + delta, 0.5), 10);
        if (next <= 1) setImgOffset({ x: 0, y: 0 });
        return next;
      });
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, []);

  if (!photos.length) return null;
  const current = photos[currentIndex];

  return (
    /* No backdrop — user can interact with the page behind */
    <div
      className="fixed z-[9999] flex flex-col bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
      style={{
        width: "min(700px, 85vw)",
        height: "min(550px, 75vh)",
        minWidth: "350px",
        minHeight: "300px",
        resize: "both",
        left: `calc(50% - min(350px, 42.5vw) + ${windowPos.x}px)`,
        top: `calc(15% + ${windowPos.y}px)`,
      }}
      onMouseDown={handleWindowMouseDown}
    >
      {/* Header — draggable handle */}
      <div
        ref={headerRef}
        className={cn(
          "flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/80 select-none flex-shrink-0",
          isDraggingWindow ? "cursor-grabbing" : "cursor-grab"
        )}
      >
        <div className="flex items-center gap-3 min-w-0">
          <span className="text-sm font-medium text-foreground truncate">
            {title}
          </span>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {currentIndex + 1} / {photos.length}
          </span>
        </div>
        <div className="flex items-center gap-0.5 flex-shrink-0">
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={zoomOut}
            title="Zoom out"
          >
            <ZoomOut className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7"
            onClick={zoomIn}
            title="Zoom in"
          >
            <ZoomIn className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-7 w-7 ml-1 hover:bg-destructive/20 hover:text-destructive"
            onClick={onClose}
            title="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Image area */}
      <div
        ref={imageAreaRef}
        className={cn(
          "flex-1 relative overflow-hidden bg-black/90 flex items-center justify-center",
          zoom > 1 ? (isDraggingImg ? "cursor-grabbing" : "cursor-grab") : ""
        )}
        onMouseDown={handleImgMouseDown}
      >
        <FirebaseImage
          src={current.url}
          alt={current.name}
          className="max-w-full max-h-full object-contain transition-transform duration-100"
          style={{
            transform: `scale(${zoom}) translate(${imgOffset.x / zoom}px, ${imgOffset.y / zoom}px)`,
          }}
          draggable={false}
        />

        {/* Prev / Next arrows */}
        {photos.length > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/50 hover:bg-black/70 text-white transition-colors"
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </>
        )}
      </div>

      {/* Footer — photo name + thumbnail strip */}
      <div className="flex-shrink-0 border-t border-border bg-muted/30 px-3 py-2">
        <p className="text-xs text-muted-foreground truncate mb-2">
          {current.name}
        </p>
        {photos.length > 1 && (
          <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-thin">
            {photos.map((photo, idx) => (
              <button
                key={idx}
                onClick={() => {
                  setCurrentIndex(idx);
                  setZoom(1);
                  setImgOffset({ x: 0, y: 0 });
                }}
                className={cn(
                  "flex-shrink-0 w-12 h-12 rounded-md overflow-hidden border-2 transition-all",
                  idx === currentIndex
                    ? "border-primary ring-1 ring-primary/30"
                    : "border-border/50 opacity-60 hover:opacity-100"
                )}
              >
                <FirebaseImage
                  src={photo.url}
                  alt={photo.name}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
